import { useEffect, useRef, useState } from 'react';
import {
  durationData,
  ISelect,
  PageToolbar,
  PeriodType,
  removeEmptyQueryParams,
  TeamMenu,
  useDebounce,
  useQueryState,
} from '@devd-client/devd/components';
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Text,
  useOutsideClick,
} from '@chakra-ui/react';
import { useAuth } from '@devd-client/devd/auth';
import moment from 'moment';
import { useDashboardStore } from '@devd-client/devd/components';
import { GoalsTable } from '@devd-client/devd/components';
import { useInView } from 'react-intersection-observer';
import { ChakraStylesConfig, Select } from 'chakra-react-select';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { filterOptions } from '../../helpers/Goals.utils';
import useContributorsDataGql from '../../graphql';
import { Contributor } from '../../helpers/goals.types';

interface OrgTabProps {
  team: string;
  setTeam: (team: string) => void;
}

export const OrgTab = ({ team, setTeam }: OrgTabProps) => {
  const [appState, ,] = useAuth();
  const { selected, setSelected } = useDashboardStore();

  const [startDate, setStartDate] = useQueryState('start');
  const [endDate, setEndDate] = useQueryState('end');
  const [duration, setDuration] = useQueryState('duration');
  const [sprintId, setSprintId] = useQueryState('sprintId');
  const [sprintName, setSprintName] = useQueryState('sprint');
  const [filterParam, setFilterParam] = useQueryState('filter');
  const [authorParam, setAuthorParam] = useQueryState('author');
  const initialSelectedTeam =
    team && team !== 'Organisation'
      ? { value: team, label: team }
      : { value: '', label: 'Organisation' };

  const [selectedTeam, setSelectedTeam] =
    useState<ISelect>(initialSelectedTeam);
  const [periodOptions, setPeriodOptions] = useState(durationData);

  useEffect(() => {
    if (typeof selected !== 'undefined') {
      selected.startDate && setStartDate(selected.startDate);
      selected.endDate && setEndDate(selected.endDate);
      selected.duration && setDuration(selected.duration);
      selected?.sprintId === ''
        ? removeEmptyQueryParams({ sprintId: '' })
        : setSprintId(selected?.sprintId);
      selected?.sprintName === ''
        ? removeEmptyQueryParams({ sprint: '' })
        : setSprintName(selected?.sprintName);
    }

    if (typeof selectedTeam !== 'undefined') {
      if (selectedTeam.label === 'Organisation') {
        setTeam('');
        removeEmptyQueryParams({ team: '' });
      } else {
        setTeam(selectedTeam.value);
      }
    }
  }, [selected, selectedTeam.value]);

  useEffect(() => {
    team &&
      setSelectedTeam(
        team === 'Organisation'
          ? { value: '', label: 'Organisation' }
          : { value: team, label: team }
      );

    if (filterParam) {
      const validFilter = filterOptions.find((f) => f.value === filterParam);
      if (validFilter) {
        setFilterBy(validFilter);
      }
    }

    if (authorParam) {
      setAuthor(authorParam);
      setSelectedContributor({
        value: authorParam,
        label: authorParam,
      });
    }

    duration || selected.duration
      ? setSelected(
          periodOptions
            .filter(
              (item: PeriodType) =>
                item.duration === duration ||
                item.duration === selected.duration
            )
            .map((el: PeriodType) =>
              el.duration === 'Custom' ||
              el.duration === 'Today' ||
              el.duration === 'Month' ||
              el.duration === 'Quarter'
                ? {
                    ...el,
                    startDate: startDate || selected.startDate,
                    endDate: endDate || selected.endDate,
                  }
                : el.duration === 'Sprint'
                ? {
                    ...el,
                    startDate: startDate || selected.startDate,
                    endDate: endDate || selected.endDate,
                    sprintId: sprintId || selected.sprintId,
                    sprintName: sprintName || selected.sprintName,
                  }
                : el
            )[0]
        )
      : appState?.user?.dto?.sprint
      ? setSelected({
          ...periodOptions[6],
          startDate: moment(
            JSON.parse(appState?.user?.dto?.sprintDetail)?.startDate
          ).format('YYYY-MM-DD'),
          endDate: moment(
            JSON.parse(appState?.user?.dto?.sprintDetail)?.endDate
          ).format('YYYY-MM-DD'),
          sprintId: appState?.user?.dto?.sprint,
          sprintName: JSON.parse(appState?.user?.dto?.sprintDetail)?.sprintName,
        })
      : setSelected(periodOptions[3]);
  }, []);

  const [filterBy, setFilterBy] = useState({
    value: 'TEAM',
    label: 'Team',
  });
  const [selectedContributor, setSelectedContributor] =
    useState<ISelect | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 500);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pageNo, setPageNo] = useState(0);
  const { ref: bottomRef, inView } = useInView({ threshold: 0.1 });
  const [allMembers, setAllMembers] = useState<Contributor[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const [author, setAuthor] = useState<string | undefined>();

  useOutsideClick({
    ref: dropdownRef,
    handler: () => {
      setIsOpen(false);
      setSearchQuery('');
    },
  });

  const { data: membersData, isLoading: membersLoading } =
    useContributorsDataGql(
      {
        teamId: '',
        startDate: null,
        endDate: null,
        contributorFilters: {
          activeFilters: null,
          inActiveFilters: null,
          excluded: 'false',
        },
        searchTerm: debouncedSearch,
      },
      pageNo,
      20
    );

  useEffect(() => {
    if (membersData?.data) {
      if (pageNo === 0) {
        setAllMembers(membersData.data);
      } else {
        setAllMembers((prev) => [...prev, ...membersData.data]);
      }

      if (membersData.currentPage + 1 >= membersData.totalPages) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    }
  }, [membersData, pageNo]);

  useEffect(() => {
    if (inView && !membersLoading && hasMore && isOpen) {
      setPageNo((prev) => prev + 1);
    }
  }, [inView, membersLoading, hasMore, isOpen]);

  useEffect(() => {
    setPageNo(0);
    setHasMore(true);
  }, [debouncedSearch]);

  const handleFilterChange = (e: ISelect) => {
    setFilterBy(e);
    setFilterParam(e.value);
    if (e.value === 'TEAM') {
      setAuthor(undefined);
      setSelectedContributor(null);
      removeEmptyQueryParams({ author: '' });
    } else if (e.value === 'CONTRIBUTOR') {
      setTeam('');
      setSelectedTeam({ value: '', label: '' });
      removeEmptyQueryParams({ team: '' });
    }
  };

  const handleTeamSelection = (teamObj: ISelect) => {
    setSelectedTeam(teamObj);
    if (teamObj.value) {
      setTeam(teamObj.value);
    } else {
      removeEmptyQueryParams({ team: '' });
    }
  };

  const handleContributorSelection = (member: Contributor) => {
    const contributor = {
      value: member?.email,
      label: member?.name || member?.email,
    };
    setSelectedContributor(contributor);
    setAuthor(member?.email);
    setAuthorParam(member?.email);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPageNo(0);
    setAllMembers([]);
    setSelectedContributor(null);
  };
  return (
    <Box>
      <PageToolbar
        leftComp={
          <Flex gap={3} align="flex-end" mb={'10px'}>
            <Box mr={3}>
              <Text
                fontSize="sm"
                fontFamily="heading"
                fontWeight="semibold"
                color="text.secondary"
                lineHeight="17px"
                mb={2}
              >
                Filter By
              </Text>
              <Select
                placeholder="Select Scope"
                useBasicStyles
                onChange={handleFilterChange}
                size="sm"
                value={filterBy}
                options={filterOptions}
                chakraStyles={{
                  menu: (provided: ChakraStylesConfig) => ({
                    ...provided,
                    zIndex: 9999,
                  }),
                }}
              />
            </Box>

            {filterBy.value === 'TEAM' && (
              <TeamMenu
                selectedTeam={selectedTeam}
                handleSelectedTeam={handleTeamSelection}
                noDefault
                hideShowDetails
              />
            )}

            {filterBy.value === 'CONTRIBUTOR' && (
              <Box mr={3} position="relative" ref={dropdownRef} width="240px">
                <Text
                  fontSize="sm"
                  fontFamily="heading"
                  fontWeight="semibold"
                  color="text.secondary"
                  lineHeight="17px"
                  mb={2}
                >
                  Contributor
                </Text>
                <InputGroup>
                  <Input
                    placeholder="Select Contributor"
                    value={searchQuery || selectedContributor?.label}
                    onChange={handleSearchChange}
                    onClick={() => setIsOpen(true)}
                    size="sm"
                    border="1px solid"
                    borderColor="text.tertiary"
                    _focus={{ borderColor: 'primary' }}
                    pr="30px"
                  />
                  <InputRightElement h="32px">
                    <ChevronDownIcon
                      color="text.secondary2"
                      transform={isOpen ? 'rotate(180deg)' : undefined}
                      transition="transform 0.2s"
                      cursor="pointer"
                      onClick={() => setIsOpen(!isOpen)}
                    />
                  </InputRightElement>
                </InputGroup>

                {isOpen && (
                  <Box
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    mt={2}
                    maxH="200px"
                    overflowY="auto"
                    backgroundColor="white"
                    borderRadius="4px"
                    boxShadow="md"
                    zIndex={9999}
                    border="1px solid"
                    borderColor="text.tertiary"
                  >
                    {membersLoading && pageNo === 0 ? (
                      <Flex justify="center" my={4}>
                        <Spinner size="sm" />
                      </Flex>
                    ) : allMembers?.length === 0 ? (
                      <Text
                        my={4}
                        color="text.secondary2"
                        fontSize={'sm'}
                        textAlign="center"
                      >
                        No results found
                      </Text>
                    ) : (
                      <>
                        {allMembers?.map(
                          (member: Contributor, index: number) => (
                            <Box
                              key={
                                member?.email ||
                                member?.name ||
                                `member-${index}`
                              }
                              p={2}
                              cursor="pointer"
                              _hover={{ backgroundColor: 'text.tertiary' }}
                              onClick={() => handleContributorSelection(member)}
                              borderBottom="1px solid"
                              borderColor="text.tertiary"
                            >
                              <Text fontSize="sm" fontWeight="medium">
                                {member?.name || member?.email}
                              </Text>
                            </Box>
                          )
                        )}
                        {membersLoading && pageNo > 0 && (
                          <Flex justify="center" my={4}>
                            <Spinner size="sm" />
                          </Flex>
                        )}
                        {membersData?.data?.length &&
                          membersData?.data?.length >= 20 && (
                            <Box ref={bottomRef} h="20px" visibility="hidden" />
                          )}
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Flex>
        }
      />
      <GoalsTable
        scope={author ? 'CONTRIBUTOR' : team ? 'TEAM' : 'ORG'}
        author={author}
        teamId={team}
      />
    </Box>
  );
};
